import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS, CHART_COLORS_ROSE } from "./reportesUtils";

applyPlugin(jsPDF);

const RecaudacionClasInm = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [recClasInmData, setRecClasInmData] = useState(null);
  const [recClasInmFechaDesde, setRecClasInmFechaDesde] = useState("");
  const [recClasInmFechaHasta, setRecClasInmFechaHasta] = useState("");
  const [recClasInmUso, setRecClasInmUso] = useState("todos");
  const [recClasInmFilters, setRecClasInmFilters] = useState({ uso: "", alicuota: "", comprobantes: "", impuesto: "", total: "", porcentaje: "" });

  useEffect(() => { fetchRecClasInmData(); }, []);

  const fetchRecClasInmData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (recClasInmFechaDesde) params.append("fecha_desde", recClasInmFechaDesde);
      if (recClasInmFechaHasta) params.append("fecha_hasta", recClasInmFechaHasta);
      if (recClasInmUso !== "todos") params.append("uso_inmueble", recClasInmUso);
      const url = `${API}/reportes/recaudacion-por-clasificador-inmuebles?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setRecClasInmData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados correctamente");
    } catch (error) {
      toast.error("Error al cargar datos de recaudación por clasificador de inmuebles");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosRecClasInm = () => {
    setRecClasInmFechaDesde("");
    setRecClasInmFechaHasta("");
    setRecClasInmUso("todos");
    setRecClasInmFilters({ uso: "", alicuota: "", comprobantes: "", impuesto: "", total: "", porcentaje: "" });
    setTimeout(() => fetchRecClasInmData(false), 0);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosRecClasInmActivos = recClasInmFechaDesde || recClasInmFechaHasta || recClasInmUso !== "todos";
  const updateRecClasInmFilter = (key, value) => setRecClasInmFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosRecClasInmColActivos = Object.values(recClasInmFilters).some(v => v !== "");

  const filteredRecClasInmFilas = recClasInmData?.filas?.filter(f => {
    const pf = recClasInmFilters;
    if (pf.uso && !(f.uso_inmueble || "").toLowerCase().includes(pf.uso.toLowerCase())) return false;
    if (pf.alicuota && !String(f.alicuota_anual || 0).includes(pf.alicuota)) return false;
    if (pf.comprobantes && !String(f.nro_comprobantes || 0).includes(pf.comprobantes)) return false;
    if (pf.impuesto && !String(f.impuesto_calculado || 0).includes(pf.impuesto)) return false;
    if (pf.total && !String(f.total_recaudado || 0).includes(pf.total)) return false;
    if (pf.porcentaje && !String(f.porcentaje || 0).includes(pf.porcentaje)) return false;
    return true;
  }) || [];

  const recClasInmTotalesFiltrados = filteredRecClasInmFilas.reduce((acc, f) => ({
    nro_comprobantes: acc.nro_comprobantes + (f.nro_comprobantes || 0),
    impuesto_calculado: acc.impuesto_calculado + (f.impuesto_calculado || 0),
    total_recaudado: acc.total_recaudado + (f.total_recaudado || 0),
  }), { nro_comprobantes: 0, impuesto_calculado: 0, total_recaudado: 0 });


  const exportRecClasInmExcel = () => {
    if (!recClasInmData) return;
    const wsData = [
      ["RECAUDACIÓN POR CLASIFICADOR DE INMUEBLES"], [],
      ["USO DEL INMUEBLE", "ALÍCUOTA ANUAL (%)", "NRO. COMP.", "IMPUESTO CALCULADO", "TOTAL RECAUDADO", "%"]
    ];
    filteredRecClasInmFilas.forEach(f => {
      wsData.push([f.uso_inmueble, f.alicuota_anual, f.nro_comprobantes, f.impuesto_calculado, f.total_recaudado, f.porcentaje]);
    });
    wsData.push([]);
    wsData.push(["TOTALES", "", recClasInmTotalesFiltrados.nro_comprobantes, recClasInmTotalesFiltrados.impuesto_calculado, recClasInmTotalesFiltrados.total_recaudado, "100%"]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "ClasifInmuebles");
    XLSX.writeFile(wb, `Recaudacion_Clasificador_Inmuebles_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportRecClasInmPDF = () => {
    if (!recClasInmData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(14);
    doc.setTextColor(225, 29, 72);
    doc.text("RECAUDACIÓN POR CLASIFICADOR DE INMUEBLES", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Total recaudado: Bs. ${recClasInmTotalesFiltrados.total_recaudado.toLocaleString("es-VE", {minimumFractionDigits: 2})}`, 14, 25);
    const headCols = ["Uso del Inmueble", "Alícuota Anual (%)", "Comp.", "Imp. Calculado", "Total Recaudado", "%"];
    const body = filteredRecClasInmFilas.map(f => [f.uso_inmueble, f.alicuota_anual + "%", f.nro_comprobantes, fmtBs(f.impuesto_calculado), fmtBs(f.total_recaudado), f.porcentaje + "%"]);
    body.push(["TOTALES", "", recClasInmTotalesFiltrados.nro_comprobantes, fmtBs(recClasInmTotalesFiltrados.impuesto_calculado), fmtBs(recClasInmTotalesFiltrados.total_recaudado), "100%"]);
    doc.autoTable({
      startY: 32, head: [headCols], body,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [225, 29, 72], textColor: 255 },
      alternateRowStyles: { fillColor: [255, 241, 242] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [254, 205, 211]; } }
    });
    doc.save(`Recaudacion_Clasificador_Inmuebles_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportRecClasInmCSV = () => {
    if (!recClasInmData) return;
    const cols = ["USO DEL INMUEBLE", "ALÍCUOTA ANUAL (%)", "NRO. COMP.", "IMPUESTO CALCULADO", "TOTAL RECAUDADO", "%"];
    const rows = filteredRecClasInmFilas.map(f => [`"${f.uso_inmueble}"`, f.alicuota_anual, f.nro_comprobantes, f.impuesto_calculado, f.total_recaudado, f.porcentaje].join(","));
    const blob = new Blob(["\ufeff" + cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Recaudacion_Clasificador_Inmuebles_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const usosDisponibles = recClasInmData?.resumen?.usos_disponibles || [];
  const chartData = filteredRecClasInmFilas.map(f => ({
    nombre: f.uso_inmueble.length > 30 ? f.uso_inmueble.substring(0, 30) + "..." : f.uso_inmueble,
    nombre_completo: f.uso_inmueble,
    total: f.total_recaudado,
    porcentaje: f.porcentaje
  }));
  return (
    <div className="space-y-6" data-testid="reporte-recaudacion-clasificador-inmuebles">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="recclsinm-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
            <Building className="w-6 h-6 text-rose-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Recaudación por Clasificador de Inmuebles
            </h1>
            <p className="text-slate-500">Ingresos recaudados agrupados por uso del inmueble y alícuota anual</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchRecClasInmData(false)} disabled={loading} data-testid="recclsinm-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!recClasInmData} data-testid="recclsinm-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportRecClasInmExcel} data-testid="recclsinm-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecClasInmPDF} data-testid="recclsinm-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportRecClasInmCSV} data-testid="recclsinm-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-rose-200 bg-rose-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[170px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Desde</Label>
              <Input type="date" value={recClasInmFechaDesde} onChange={(e) => setRecClasInmFechaDesde(e.target.value)} className="bg-white" data-testid="recclsinm-fecha-desde" />
            </div>
            <div className="w-[170px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Fecha Hasta</Label>
              <Input type="date" value={recClasInmFechaHasta} onChange={(e) => setRecClasInmFechaHasta(e.target.value)} className="bg-white" data-testid="recclsinm-fecha-hasta" />
            </div>
            <div className="w-[250px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Uso del Inmueble</Label>
              <Select value={recClasInmUso} onValueChange={setRecClasInmUso}>
                <SelectTrigger className="bg-white" data-testid="recclsinm-uso">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {usosDisponibles.map(u => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchRecClasInmData(true)} disabled={loading} className="bg-rose-600 hover:bg-rose-700" data-testid="recclsinm-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosRecClasInmActivos && (
                <Button variant="outline" onClick={limpiarFiltrosRecClasInm} disabled={loading} data-testid="recclsinm-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-rose-400" />
        </div>
      ) : recClasInmData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Recaudado</p>
                <p className="text-lg font-bold text-rose-700" data-testid="recclsinm-card-total">{fmtBs(recClasInmData.resumen?.total_recaudado)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Total Comprobantes</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="recclsinm-card-comprobantes">{recClasInmData.resumen?.total_comprobantes || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Clasificadores</p>
                <p className="text-2xl font-bold text-rose-600" data-testid="recclsinm-card-clasificadores">{recClasInmData.resumen?.total_clasificadores || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Promedio por Clasificador</p>
                <p className="text-lg font-bold text-amber-600" data-testid="recclsinm-card-promedio">{fmtBs(recClasInmData.resumen?.promedio_por_clasificador)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de barras horizontal */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución por Uso del Inmueble</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ width: "100%", height: Math.max(chartData.length * 50 + 40, 200) }} data-testid="recclsinm-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tickFormatter={(val) => `Bs. ${(val / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="nombre" width={240} tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(val) => [`Bs. ${val.toLocaleString("es-VE", {minimumFractionDigits: 2})}`, "Recaudado"]}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.nombre_completo || label}
                      />
                      <Bar dataKey="total" name="Total Recaudado" radius={[0, 6, 6, 0]}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS_ROSE[index % CHART_COLORS_ROSE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabla */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle por Clasificador de Inmuebles</CardTitle>
                  <CardDescription>{filteredRecClasInmFilas.length} de {recClasInmData.filas?.length || 0} clasificadores</CardDescription>
                </div>
                {hayFiltrosRecClasInmColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setRecClasInmFilters({ uso: "", alicuota: "", comprobantes: "", impuesto: "", total: "", porcentaje: "" })} data-testid="recclsinm-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="recclsinm-tabla">
                  <thead>
                    <tr className="border-b-2 border-rose-200 bg-rose-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-rose-800 uppercase">Uso del Inmueble</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-rose-800 uppercase">Alícuota Anual (%)</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-rose-800 uppercase">Comp.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-rose-800 uppercase">Impuesto Calculado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-rose-800 uppercase">Total Recaudado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-rose-800 uppercase">%</th>
                    </tr>
                    <tr className="bg-rose-50/60 border-b border-rose-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={recClasInmFilters.uso} onChange={(e) => updateRecClasInmFilter("uso", e.target.value)} data-testid="recclsinm-filter-uso" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recClasInmFilters.alicuota} onChange={(e) => updateRecClasInmFilter("alicuota", e.target.value)} data-testid="recclsinm-filter-alicuota" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recClasInmFilters.comprobantes} onChange={(e) => updateRecClasInmFilter("comprobantes", e.target.value)} data-testid="recclsinm-filter-comprobantes" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recClasInmFilters.impuesto} onChange={(e) => updateRecClasInmFilter("impuesto", e.target.value)} data-testid="recclsinm-filter-impuesto" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recClasInmFilters.total} onChange={(e) => updateRecClasInmFilter("total", e.target.value)} data-testid="recclsinm-filter-total" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={recClasInmFilters.porcentaje} onChange={(e) => updateRecClasInmFilter("porcentaje", e.target.value)} data-testid="recclsinm-filter-porcentaje" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecClasInmFilas.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-slate-400">No se encontraron datos de recaudación por clasificador de inmuebles</td></tr>
                    ) : (
                      filteredRecClasInmFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-rose-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-800">{f.uso_inmueble}</span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700 font-mono">{f.alicuota_anual > 0 ? f.alicuota_anual + "%" : "-"}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{f.nro_comprobantes}</td>
                          <td className="py-2.5 px-3 text-sm text-right text-slate-700">{fmtBs(f.impuesto_calculado)}</td>
                          <td className="py-2.5 px-3 text-sm text-right font-bold text-rose-700">{fmtBs(f.total_recaudado)}</td>
                          <td className="py-2.5 px-3 text-sm text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-slate-100 rounded-full h-2">
                                <div className="bg-rose-500 h-2 rounded-full" style={{ width: `${Math.min(f.porcentaje, 100)}%` }}></div>
                              </div>
                              <span className="text-xs font-semibold text-slate-600 w-10 text-right">{f.porcentaje}%</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredRecClasInmFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-rose-100 border-t-2 border-rose-300">
                        <td colSpan={2} className="py-3 px-3 text-sm font-bold text-rose-900 uppercase">Totales ({filteredRecClasInmFilas.length} clasificadores)</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-rose-900">{recClasInmTotalesFiltrados.nro_comprobantes}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-rose-900">{fmtBs(recClasInmTotalesFiltrados.impuesto_calculado)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-rose-900">{fmtBs(recClasInmTotalesFiltrados.total_recaudado)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-rose-900">100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchRecClasInmData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default RecaudacionClasInm;
